import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import axios from 'axios';

import { BASEURL } from '../constant';
import getApi from '../api/getApi';
import postApi from '../api/postApi';
import { getAccessToken, handleUnauthorized } from '../auth';
import { getUserFacingError } from '../api/userFacingError';

type BandMappingRow = {
  id: number;
  cell_prefix: string;
  technology: string;
  band_name: string;
};

type TechnologyFilter = 'lte' | 'nr';

const emptyForm = {
  cell_prefix: '',
  technology: 'lte' as TechnologyFilter,
  band_name: '',
};

export default function BandMapping() {
  const role = localStorage.getItem('role');
  const isAdmin = role === 'admin';

  const [technology, setTechnology] = useState<TechnologyFilter>('lte');
  const [rows, setRows] = useState<BandMappingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const loadMappings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getApi(`${BASEURL}/band_mappings?technology=${technology}`);
      if (res?.status_code === 200) {
        setRows(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res?.message || 'Failed to load band mappings');
        setRows([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load band mappings');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [technology]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, technology });
    setMessage('');
    setError('');
    setShowModal(true);
  };

  const openEdit = (row: BandMappingRow) => {
    setEditingId(row.id);
    setForm({
      cell_prefix: row.cell_prefix,
      technology: row.technology as TechnologyFilter,
      band_name: row.band_name,
    });
    setMessage('');
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    setMessage('');
    setError('');
    const prefix = form.cell_prefix.trim().toUpperCase();
    const bandName = form.band_name.trim();
    if (!prefix || prefix.length !== 1 || !/^[A-Z]$/.test(prefix)) {
      setError('Cell prefix must be a single letter A–Z');
      return;
    }
    if (!bandName) {
      setError('Band name is required');
      return;
    }

    const payload = {
      cell_prefix: prefix,
      technology: form.technology,
      band_name: bandName,
    };

    try {
      if (editingId === null) {
        const res = await postApi(`${BASEURL}/band_mappings`, payload);
        if (res?.data?.status_code === 201) {
          setMessage(res.data.message || 'Band mapping created');
          closeModal();
          loadMappings();
        } else {
          setError(res?.data?.message || 'Create failed');
        }
      } else {
        const res = await axios.put(`${BASEURL}/band_mappings/${editingId}`, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
        });
        if (res?.data?.status_code === 200) {
          setMessage(res.data.message || 'Band mapping updated');
          closeModal();
          loadMappings();
        } else {
          setError(res?.data?.message || 'Update failed');
        }
      }
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          handleUnauthorized();
          return;
        }
        setError(getUserFacingError(err));
      } else {
        setError(err.message || 'Request failed');
      }
    }
  };

  const handleDelete = async (row: BandMappingRow) => {
    if (!window.confirm(`Delete mapping ${row.cell_prefix} → ${row.band_name}?`)) {
      return;
    }
    setMessage('');
    setError('');
    try {
      const res = await axios.delete(`${BASEURL}/band_mappings/${row.id}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      if (res?.data?.status_code === 200) {
        setMessage(res.data.message || 'Band mapping deleted');
        loadMappings();
      } else {
        setError(res?.data?.message || 'Delete failed');
      }
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          handleUnauthorized();
          return;
        }
        setError(getUserFacingError(err));
      } else {
        setError(err.message || 'Delete failed');
      }
    }
  };

  return (
    <div className="row admin-page">
      <div className="col-12 registration-text">
        <h2>Band Mapping</h2>
        <p className="text-muted">
          CELL first-letter to band name (Power BI equivalent). Applied automatically on cell KPI upload.
        </p>
      </div>

      <div className="col-12 mb-3">
        <div className="btn-group" role="group">
          <Button
            variant={technology === 'lte' ? 'primary' : 'outline-primary'}
            onClick={() => setTechnology('lte')}
          >
            LTE
          </Button>
          <Button
            variant={technology === 'nr' ? 'primary' : 'outline-primary'}
            onClick={() => setTechnology('nr')}
          >
            NR (5G)
          </Button>
        </div>
        {isAdmin && (
          <Button className="ms-3" variant="success" onClick={openCreate}>
            Add mapping
          </Button>
        )}
      </div>

      {message && <div className="col-12 alert alert-success">{message}</div>}
      {error && <div className="col-12 alert alert-danger">{error}</div>}

      <div className="col-12">
        {loading ? (
          <p>Loading…</p>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Letter</th>
                <th>Technology</th>
                <th>Band name</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 4 : 3} className="text-center text-muted">
                    No mappings for {technology.toUpperCase()}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.cell_prefix}</td>
                    <td>{row.technology.toUpperCase()}</td>
                    <td>{row.band_name}</td>
                    {isAdmin && (
                      <td>
                        <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(row)}>
                          Delete
                        </Button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

      {isAdmin && (
        <Modal show={showModal} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editingId === null ? 'Add band mapping' : 'Edit band mapping'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Cell prefix (first letter of CELL)</Form.Label>
                <Form.Control
                  maxLength={1}
                  value={form.cell_prefix}
                  onChange={(e) => setForm({ ...form, cell_prefix: e.target.value.toUpperCase() })}
                  placeholder="e.g. L"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Technology</Form.Label>
                <Form.Select
                  value={form.technology}
                  onChange={(e) =>
                    setForm({ ...form, technology: e.target.value as TechnologyFilter })
                  }
                >
                  <option value="lte">LTE</option>
                  <option value="nr">NR (5G)</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Band name</Form.Label>
                <Form.Control
                  value={form.band_name}
                  onChange={(e) => setForm({ ...form, band_name: e.target.value })}
                  placeholder="e.g. L2100"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
